/// <amd-module name="VSS/Features/Markdown/Plugins/TableOfContentsPreProcessor" />
import * as MarkdownIt from "markdown-it";
import { MarkdownConstants } from "../MarkdownConstants";

/**
 * Adds toc marker pattern in the content to generate table of contents
 * If two toc markers are present then it just keeps the first one. Same for tosp marker.
 **/
export class TableOfContentsPreProcessor {
    private _tocMarker: string;
    private _tospMarker: string;
    private _noTocMarker: string;

    constructor(md: MarkdownIt.MarkdownIt, tocMarker?: string, noTocMarker?: string, tospMarker?: string) {
        md.core.ruler.after("block", "markdown_toc", this._contentTransformer);

        this._tocMarker = tocMarker || MarkdownConstants.TOCDefaultMarker;
        this._tospMarker = tospMarker || MarkdownConstants.TOSPDefaultMarker;
        this._noTocMarker = noTocMarker || MarkdownConstants.NoTOCDefaultMarker;
    }

    private _contentTransformer = (state: { tokens: MarkdownIt.Token[]; }): void  => {
        let headerCount = 0;
        let noTocTokenIndex = -1;
        let firstTocTokenIndex = -1;
        let firstTospTokenIndex = -1;

        // Going to -2 because we are looking ahead
        for (var i = 0; i < state.tokens.length - 2; i++) {
            if (state.tokens[i].type === "paragraph_open"
                && state.tokens[i + 1].type === "inline") {

                if (this._isTokenContainsLine(state.tokens, i + 1, this._noTocMarker)) {
                    noTocTokenIndex = i + 1;
                    // Remove [[_NOTOC_]] syntax in the content
                    state.tokens[i + 1].content = this._replaceAll(state.tokens[i + 1].content, this._noTocMarker, "");
                }
                firstTocTokenIndex = this._handleMarker(state.tokens, i, this._tocMarker, firstTocTokenIndex);
                firstTospTokenIndex = this._handleMarker(state.tokens, i, this._tospMarker, firstTospTokenIndex);
            } else if (state.tokens[i].type === "heading_open"
                && state.tokens[i + 1].type === "inline") {

                headerCount++;
            }
        }

        if (firstTocTokenIndex >= 0) {
            this._removeRequiredMarkers(state.tokens, firstTocTokenIndex, this._tocMarker, noTocTokenIndex >= 0 || headerCount === 0);
        }

        if (firstTospTokenIndex >= 0) {
            this._removeRequiredMarkers(state.tokens, firstTospTokenIndex, this._tospMarker);
        }
    }

    private _removeRequiredMarkers(tokens: MarkdownIt.Token[], firstMarkerIndex: number, marker: string, shouldRemoveAllOccurrences?: boolean) {
        const positionOfFirstTocInSameToken = tokens[firstMarkerIndex].content.indexOf(marker);
        const positionOfLastTocInSameToken = tokens[firstMarkerIndex].content.lastIndexOf(marker);

        if (shouldRemoveAllOccurrences) {
            tokens[firstMarkerIndex].content = this._replaceAll(tokens[firstMarkerIndex].content, marker, "");
        } else if (positionOfFirstTocInSameToken !== positionOfLastTocInSameToken) {
            // Remove occurrences of marker other than the first one.
            const contentPrefix = tokens[firstMarkerIndex].content.substr(0, positionOfFirstTocInSameToken + 1);
            const processedContentSuffix = this._replaceAll(tokens[firstMarkerIndex].content.substr(positionOfFirstTocInSameToken + 1), marker, "");

            tokens[firstMarkerIndex].content = contentPrefix + processedContentSuffix;
        }
    }

    private _handleMarker(tokens: MarkdownIt.Token[], index: number, marker: string, firstMarkerIndex: number): number {
        if (!this._isTokenContainsLine(tokens, index + 1, marker)) return firstMarkerIndex;
        if (firstMarkerIndex === -1) return index + 1;
        // Remove additional markers in the content
        tokens[index + 1].content = this._replaceAll(tokens[index + 1].content, marker, "");
        return firstMarkerIndex;
    }

    private _isTokenContainsLine = (tokens: MarkdownIt.Token[], index: number, line: string): boolean =>
        tokens[index].content.indexOf(line) !== -1;

    private _replaceAll(content: string, pattern: string, replacement: string): string {
        return content.split(pattern).join(replacement);
    }
}
